import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from 'react';
import '../CSS/Global.css';
import { useSidebar } from '../SidebarContext';
import Header from './Header';
import Sidebar from './SideBar';
import DeliveryModal from './DeliveryModal';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getDecryptedUserFromCookie } from './utils/getCookie';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, LucideToggleLeft, LucideToggleRight, Pencil, Trash2 } from 'lucide-react';
import { useSearch } from '../SearchContext';
import Loader from './Loader';
import { decryptToken } from './utils/getToken';

const Delivery = () => {


    // State Variables
    const { globalSearch } = useSearch();
    const [searchQuery, setSearchQuery] = useState('');
    const [deliveries, setDeliveries] = useState([]);
    const [filteredDeliveries, setFilteredDeliveries] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const user = getDecryptedUserFromCookie("gta_mainstateuser");
    const [delivery, setDelivery] = useState({
        min_kilometer: "",
        max_kilometer: "",
        delivery_price: "",
        status: { value: 'Active', label: 'Active' },
        user_id: user.main_id,
        user_name: user.full_name || 'Admin'
    });
    const urlmain = API_BASE_URL
    const token = decryptToken("accessToken") || ""
    const [load, setLoad] = useState(false);
    const [loadButton, setLoadButton] = useState(false);


    // Effect to handle filtering
    useEffect(() => {
        const filtered = deliveries.filter((delivery) =>
            Object.values(delivery).some((value) =>
                String(value).toLowerCase().includes(globalSearch.toLowerCase())
            )
        );
        setFilteredDeliveries(filtered);
        setCurrentPage(1);
    }, [globalSearch, deliveries]);

    // Pagination Logic
    const totalPages = filteredDeliveries.length ? Math.ceil(filteredDeliveries.length / itemsPerPage) : 1;
    const currentData = filteredDeliveries.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Handlers
    const handleSearch = (e) => setSearchQuery(e.target.value);
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setDelivery((prev) => ({ ...prev, [name]: value }));
    };

    // Toggle Status
    const toggleStatus = (index) => {
        const originalIndex = deliveries.findIndex(delivery => delivery === filteredDeliveries[index]);
        const updatedDeliveries = [...deliveries];
        updatedDeliveries[originalIndex].status = !updatedDeliveries[originalIndex].status;
        setDeliveries(updatedDeliveries);
    };

    // Handle Delete
    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${urlmain}/delivery-price/${id}`, {
                        method: 'DELETE',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    });

                    const res = await response.json();

                    if (response.ok) {
                        setDeliveries(prev => prev.filter(p => p.id !== id));
                        Swal.fire('Deleted!', res.message || 'Delivery has been deleted.', 'success');
                    } else {
                        Swal.fire('Error!', res.message || 'Failed to delete pincode.', 'error');
                    }
                } catch (error) {
                    console.error('Delete failed', error);
                    Swal.fire('Error!', 'Something went wrong.', 'error');
                }
            }
        });
    };

    const statusOptions = [
        { value: 'Active', label: 'Active' },
        { value: 'Inactive', label: 'Inactive' },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoadButton(true);
        const payload = {
            min_kilometer: parseFloat(delivery.min_kilometer),
            max_kilometer: parseFloat(delivery.max_kilometer),
            delivery_price: parseFloat(delivery.delivery_price),
            status: delivery.status?.value,
            user_id: user.main_id,
            user_name: user.full_name || 'Admin'
        };

        if (payload.min_kilometer >= payload.max_kilometer) {
            toast.error("Min kilometer must be less than max kilometer", { position: "top-center" });
            return;
        }

        const isEdit = !!delivery.id;
        const url = isEdit
            ? `${urlmain}/delivery-price/${delivery.id}`
            : `${urlmain}/delivery-price`;
        const method = isEdit ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (res.ok) {
                if (isEdit) {
                    // ✅ Update only the edited item
                    setDeliveries(prev =>
                        prev.map(d =>
                            d.id === delivery.id ? { ...payload, id: delivery.id } : d
                        )
                    );
                    toast.success("Delivery Updated", { position: "top-center" });
                } else {
                    // ✅ Append new item
                    setDeliveries(prev => [...prev, { ...payload, id: data.data?.id || Date.now() }]);
                    toast.success("Delivery Added", { position: "top-center" });
                }

                // Close modal without resetting pagination
                setIsModalOpen(false);
                setDelivery({ min_kilometer: "", max_kilometer: "", delivery_price: "", status: { value: 'Active', label: 'Active' } });
            } else {
                toast.error(data.message || "Operation failed");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Unexpected error occurred");
        }finally{
            setLoadButton(false)
        }
    };

    const fetchDelivery = async () => {
        setLoad(true)
        try {
            const response = await fetch(`${urlmain}/delivery-price/${user.main_id}`)
            const res = await response.json();
            setDeliveries(res.data);
        } catch (error) {
            console.error(error)
        } finally {
            setLoad(false)
        }
    }


    useEffect(() => {
        fetchDelivery();
    }, [])

    if (load) {
        return (
            <Loader />
        );
    }


    return (
        <>
            <div className="oms-header">
                <h1 className="dashboard-title">Delivery</h1>
                <button className="oms-add-btn" onClick={() => {
                    setIsModalOpen(true)

                    setDelivery({
                        min_kilometer: "",
                        max_kilometer: "",
                        delivery_price: "",
                        status: "",
                        user_id: user.main_id,
                        user_name: user.full_name || 'Admin'
                    })
                }}>
                    Add delivery
                </button>
            </div>

            {/* Search Filter */}
            {/* <div className="filter-search">

                    <div className="filter-search-input">
                        <input
                            type="search"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                        <i className="fa fa-search"></i> 
                    </div>
                </div> */}

            {/* Customer Table */}
            <div className="customer-table responsive-table">
                <table>
                    <thead>
                        <tr>
                            <th>Delivery Price</th>
                            <th>Min Kilometer</th>
                            <th>Max Kilometer</th>
                            {/* <th>Status</th> */}
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.length > 0 ? (
                            currentData.map((customer, index) => (
                                <tr key={index}>
                                    <td>{customer.delivery_price}</td>
                                    <td>{customer.min_kilometer}</td>
                                    <td>{customer.max_kilometer}</td>
                                    {/* <td>
                                            <button
                                                className={`status-toggle ${customer.status ? 'active' : 'inactive'}`}
                                                onClick={() => toggleStatus(index)}
                                            >
                                                {customer.status ? (
                                                    <i className="fas fa-toggle-on"></i>
                                                ) : (
                                                    <i className="fas fa-toggle-off"></i>
                                                )}
                                            </button>
                                        </td> */}
                                    <td>
                                        <button className="edit-button" onClick={() => {
                                            setIsModalOpen(true)
                                            setDelivery({
                                                id: customer.id,
                                                min_kilometer: customer.min_kilometer,
                                                max_kilometer: customer.max_kilometer,
                                                delivery_price: customer.delivery_price,
                                                status: statusOptions.find(opt => opt.value === customer.status),
                                                user_id: user.main_id,
                                                user_name: user.full_name || 'Admin'
                                            })
                                        }}>
                                            <i className="fa-solid fa-pencil edit-oms"></i>
                                        </button>
                                        <button
                                            className="delete-button"
                                            onClick={() => handleDelete(customer.id)}
                                        >
                                            <i className="fa-solid fa-trash delete-oms"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{ textAlign: 'center' }}>No delivery found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="ecom-responsive-cards">
                {currentData.length === 0 ? (
                    <p style={{ textAlign: "center", margin: "50px" }}>No Delivery Added</p>
                ) : (
                    currentData.map((group, index) => (
                        <div className="ecom-responsive-card" key={index}>
                            <div className="ecom-responsive-header">

                                <p onClick={() => {
                                    setDelivery({
                                        id: group.id,
                                        min_kilometer: group.min_kilometer,
                                        max_kilometer: group.max_kilometer,
                                        delivery_price: group.delivery_price,
                                        status: statusOptions.find(opt => opt.value === group.status),
                                        user_id: user.main_id,
                                        user_name: user.full_name || 'Admin'
                                    });
                                    setIsModalOpen(true);
                                }}>
                                    <Pencil size={20} style={{ marginRight: 4 }} /> <span>Edit</span>
                                </p>


                                <p onClick={() => handleDelete(group.id)}>
                                    <Trash2 size={20} style={{ marginRight: 4 }} /> <span>Delete</span>
                                </p>
                            </div>

                            <div className="ecom-responsive-body">
                                <div className="ecom-responsive-left">
                                    <h2>₹{group.delivery_price || "Not Provided"}</h2>
                                    <p>Min Kilometer: {group.min_kilometer}</p>
                                    <p>Max Kilometer: {group.max_kilometer}</p>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {totalPages > 1 && (

                <div className="pagination">
                    {/* First Page */}
                    <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronsLeft size={18} />
                    </button>

                    {/* Previous Page */}
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    {/* Current Page Number Only */}
                    <button className="pagination-btn active">
                        {currentPage}
                    </button>

                    {/* Next Page */}
                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronRight size={18} />
                    </button>

                    {/* Last Page */}
                    <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronsRight size={18} />
                    </button>
                </div>
            )}

            <DeliveryModal
                isOpen={isModalOpen}
                setIsOpen={setIsModalOpen}
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                handleSelectChange={(selectedOption) =>
                    setDelivery((prev) => ({ ...prev, status: selectedOption }))
                }
                delivery={delivery}
                statusOptions={statusOptions}
                loadButton={loadButton}
            />

        </>
    );
};

export default Delivery;
